import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import axios from "axios";
import Button from "../../../../common/Button";
import { updateInterventionAssessment } from "../../../../../redux/modules/intervention/actions";
import { InterventionStepsEnum } from "../../../text/data";
import { useStepNavigation } from "./useStepNavigation";
import CustomInput from "../../../../common/CustomInput";
import PharmacistTipTapEditor from "../../../../common/PharmacistTipTapEditor";
import NewToolTip from "../../../../common/New-ToolTip";
import { useMonographStatuses } from "../../../../../hooks/useMonographStatuses";
import { LaboratoryRecommendationOptions } from "../data";

const RxVigilanceDinSearch = ({ din }) => {
  const { data: monographStatusByIdentifier } = useMonographStatuses([din]);

  const vigilanceEnv = import.meta.env.VITE_VIGILANCE_ENV || "int";
  const rxVigilanceBaseUrl = `https://rx.${vigilanceEnv}.vigilance.ca`;
  const rxVigilanceAuthUrl = `https://rxauth.${vigilanceEnv}.vigilance.ca`;

  const monographStatus =
    din != null ? monographStatusByIdentifier[String(din)] : null;
  const hasMonograph = monographStatus?.hasMonograph;
  const monographId = monographStatus?.monographId;

  const handleClick = async () => {
    if (!hasMonograph || !monographId) {
      return;
    }
    try {
      const tokenResponse = await axios.post(
        `${rxVigilanceAuthUrl}/token`,
        new URLSearchParams({
          client_id: import.meta.env.VITE_APP_VIGILANCE_ID,
          client_secret: import.meta.env.VITE_APP_VIGILANCE_SECRET,
          user_id: "REEL01",
          grant_type: "client_credentials",
        }),
        { headers: { "Content-Type": "application/x-www-form-urlencoded" } }
      );
      const token = tokenResponse.data.access_token;
      const form = document.createElement("form");
      form.method = "POST";
      form.action = `${rxVigilanceBaseUrl}/module/mono/en/${monographId}.htm`;
      form.target = "_blank";
      const tokenInput = document.createElement("input");
      tokenInput.type = "hidden";
      tokenInput.name = "token";
      tokenInput.value = token;
      form.appendChild(tokenInput);
      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);
    } catch (error) {
      console.error("Failed to open monograph:", error);
    }
  };

  const tooltipId = `monograph-pharmacist-${din}`;

  return (
    <>
      <img
        src="/icons/rx-vigilance-icon.svg"
        alt="DIN Monograph"
        data-tooltip-id={tooltipId}
        className="cursor-pointer w-10 min-w-[40px]"
        onClick={handleClick}
      />
      {(!din || (monographStatus && !hasMonograph)) && (
        <NewToolTip
          id={tooltipId}
          place="bottom"
          render={() => (
            <div className="flex flex-col max-w-md p-2 bg-gray-20">
              <p className="text-sm text-teal-600">No monograph available</p>
            </div>
          )}
        />
      )}
    </>
  );
};

const PharmacistFollowUp = ({ intervention, currentInterventionSteps, currentStep: activeStep, onNavigate }) => {
  const dispatch = useDispatch();
  const { interventionId } = useParams();
  const { nextStep, previousStep } = useStepNavigation(
    InterventionStepsEnum.PharmacistFollowUp,
    currentInterventionSteps,
    activeStep
  );
  const { medications } = useSelector((state) => state.clinicalService);

  const medicationIdForMonograph =
    intervention?.interventionAssessment?.interventionAssessmentForm
      ?.interventionDetails?.medicationId;
  const dinForMonograph =
    medicationIdForMonograph && Array.isArray(medications) && medications.length > 0
      ? (medications.find((m) => m.id === medicationIdForMonograph)?.medicationGenCode ??
          medications.find((m) => m.id === medicationIdForMonograph)?.MedicationGenCode ??
          medications.find((m) => m.id === medicationIdForMonograph)?.din ??
          medications.find((m) => m.id === medicationIdForMonograph)?.DIN ??
          null)
      : null;

  // State management for form data
  const [additionalClinicalIssues, setAdditionalClinicalIssues] = useState([]);
  const [otherIssue, setOtherIssue] = useState("");
  const [laboratoryRecommendations, setLaboratoryRecommendations] = useState("");
  const [suggestedLabDateTime, setSuggestedLabDateTime] = useState("");
  const [reasonsForLab, setReasonsForLab] = useState([]);
  const [pharmacistRecommendations, setPharmacistRecommendations] = useState("");
  const [pharmacistFollowUpDate, setPharmacistFollowUpDate] = useState("");
  const otherIssueRef = useRef(null);

  // Initialize state from intervention data
  useEffect(() => {
    if (intervention?.interventionAssessment?.interventionAssessmentForm?.pharmacistFollowUp) {
      const pharmacistFollowUp = intervention.interventionAssessment.interventionAssessmentForm.pharmacistFollowUp;
      
      setAdditionalClinicalIssues(pharmacistFollowUp.additionalClinicalIssues || []);
      setOtherIssue(pharmacistFollowUp.otherIssue || "");
      setLaboratoryRecommendations(pharmacistFollowUp.laboratoryRecommendations || "");
      
      // Store dates as simple strings like ThirdPartyCreate.jsx
      setSuggestedLabDateTime(pharmacistFollowUp.suggestedLabDateTime || "");
      
      setReasonsForLab(pharmacistFollowUp.reasonsForLab || []);
      setPharmacistRecommendations(pharmacistFollowUp.pharmacistRecommendations || "");
      
      setPharmacistFollowUpDate(pharmacistFollowUp.pharmacistFollowUpDate || "");
    }
  }, [intervention]);

  useEffect(() => {
    if (!additionalClinicalIssues.includes("Other")) return;

    const frame = window.requestAnimationFrame(() => {
      otherIssueRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    });

    return () => window.cancelAnimationFrame(frame);
  }, [additionalClinicalIssues]);

  // Helper function to get medication display name
  const getMedicationDisplayName = () => {
    if (intervention?.interventionAssessment?.interventionAssessmentForm?.interventionDetails) {
      const details = intervention.interventionAssessment.interventionAssessmentForm.interventionDetails;
      return `${details.medicationName || 'Unknown Medication'} ${details.route || ''} [${details.dosage || ''} ${details.form || ''} ${details.frequency || ''}]`.trim();
    }
    return "No medication data available";
  };

  // Checkbox change handlers
  const handleClinicalIssueChange = (issueName) => {
    if (additionalClinicalIssues.includes(issueName)) {
      setAdditionalClinicalIssues(additionalClinicalIssues.filter(item => item !== issueName));
      // Clear other issue text if "Other" is unchecked
      if (issueName === "Other") {
        setOtherIssue("");
      }
    } else {
      setAdditionalClinicalIssues([...additionalClinicalIssues, issueName]);
    }
  };

  const handleLabReasonChange = (reasonName) => {
    if (reasonsForLab.includes(reasonName)) {
      setReasonsForLab(reasonsForLab.filter(item => item !== reasonName));
    } else {
      setReasonsForLab([...reasonsForLab, reasonName]);
    }
  };

  // Navigation functions
  const onNext = async () => {
    try {
      // Prepare the data to save
      const assessmentData = {
        currentStep: nextStep,
        pharmacistFollowUp: {
          additionalClinicalIssues: additionalClinicalIssues,
          otherIssue: otherIssue,
          laboratoryRecommendations: laboratoryRecommendations,
          suggestedLabDateTime: suggestedLabDateTime || null,
          reasonsForLab: reasonsForLab,
          pharmacistRecommendations: pharmacistRecommendations,
          pharmacistFollowUpDate: pharmacistFollowUpDate || null
        }
      };

      // Persist first; only move to the next tab once the save succeeds.
      const result = await dispatch(updateInterventionAssessment(interventionId, assessmentData));
      if (result) {
        onNavigate?.(nextStep);
      }
    } catch (error) {
      console.error("Failed to update intervention step:", error);
    }
  };

  const onPrevious = async () => {
    try {
      // Prepare the data to save
      const assessmentData = {
        currentStep: previousStep,
        pharmacistFollowUp: {
          additionalClinicalIssues: additionalClinicalIssues,
          otherIssue: otherIssue,
          laboratoryRecommendations: laboratoryRecommendations,
          suggestedLabDateTime: suggestedLabDateTime || null,
          reasonsForLab: reasonsForLab,
          pharmacistRecommendations: pharmacistRecommendations,
          pharmacistFollowUpDate: pharmacistFollowUpDate || null
        }
      };

      // Persist first; only move to the previous tab once the save succeeds.
      const result = await dispatch(updateInterventionAssessment(interventionId, assessmentData));
      if (result) {
        onNavigate?.(previousStep);
      }
    } catch (error) {
      console.error("Failed to update intervention step:", error);
    }
  };

  // Determine if buttons should be enabled
  const canGoNext = true; // Add your business logic here
  const canGoPrevious = true; // Can go back to step 2
  if (!intervention) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="text-gray-400 text-xl font-medium mb-4">
            No Intervention Data
          </div>
          <div className="text-gray-500 text-base">
            Pharmacist Follow Up information is not available.
          </div>
        </div>
      </div>
    );
  }

  // Clinical issues options - matching backend property names

  // Use the centralized laboratory recommendation options
  const laboratoryOptions = LaboratoryRecommendationOptions;

  const clinicalIssuesOptions = [
    { value: "Drug - Drug Interaction", label: "Drug - Drug Interaction" },
    { value: "Drug - Disease Interaction", label: "Drug - Disease Interaction" },
    { value: "Adverse Drug Interaction", label: "Adverse Drug Interaction" },
    { value: "Drug Level Monitoring (narrow therapeutic index)", label: "Drug Level Monitoring (narrow therapeutic index)" },
    { value: "Therapy Change / Tolerance/ Effectiveness", label: "Therapy Change / Tolerance/ Effectiveness" },
    { value: "Medication Adherence", label: "Medication Adherence" },
    { value: "Multiple Drugs (Poly Pharmacy)", label: "Multiple Drugs (Poly Pharmacy)" },
    { value: "Complex Medication Regimen", label: "Complex Medication Regimen" },
    { value: "Recent or Frequent Falls", label: "Recent or Frequent Falls" },
    { value: "Frequent Hospital Admissions", label: "Frequent Hospital Admissions" },
    { value: "Chronic Disease Management", label: "Chronic Disease Management" },
    { value: "Under Treated Conditions", label: "Under Treated Conditions" },
    { value: "Untreated Conditions", label: "Untreated Conditions" },
    { value: "Other", label: "Other" }
  ];

  const labReasonsOptions = [
    { value: "Monitor Drug Therapy / Effectiveness of Therapy", label: "Monitor Drug Therapy / Effectiveness of Therapy" },
    { value: "Monitor Drug Interactions / Levels", label: "Monitor Drug Interactions / Levels" },
    { value: "Monitor Disease / Chronic Conditions", label: "Monitor Disease / Chronic Conditions" },
    { value: "Determine Risk Status for Disease / Conditions", label: "Determine Risk Status for Disease / Conditions" }
  ];

  return (
    <>
      <div className="w-full max-h-fit overflow-x-hidden overflow-auto">
        <div className="flex flex-col xl:flex-row gap-8 py-3 flex-1 min-w-0">
          <div className="w-full xl:flex-[0.88] min-w-0 h-[645px] rounded-[4px] border border-gray-100 bg-gray-10 flex flex-col box-border">
            <div className="w-full min-w-0 h-[72px] min-h-[72px] max-h-[72px] box-border gap-[10px] opacity-1 pt-[12px] pr-[24px] pb-[12px] pl-[24px] border-t-[1px] border-t-gray-100 border-t-l-[4px] border-t-r-[4px] bg-gray-20 border border-gray-100 flex items-center justify-between">
              <h3 className="text-teal-300 font-semibold text-xl">
                Medication / Medical Condition Issues (Optional)
              </h3>
            </div>
          
            <div
              className={`flex-1 min-h-0 p-6 ${
                additionalClinicalIssues.includes("Other")
                  ? "overflow-y-auto"
                  : "overflow-hidden"
              }`}
            >
              <div className="mb-2">
                <div className="flex items-center justify-between gap-3">
                  <div className="font-semibold text-gray-800 whitespace-normal break-words min-w-0">
                    {getMedicationDisplayName()}
                  </div>
                  <div className="flex-shrink-0">
                    <RxVigilanceDinSearch din={dinForMonograph} />
                  </div>
                </div>
              <p className="text-sm font-semibold text-gray-700 mt-4">Select all that apply (Optional).</p>
              </div>

              <div className="w-full min-w-0 h-[149px] gap-[4px] opacity-1 pb-[13px] border-b border-b-gray-100 mb-4">
                {clinicalIssuesOptions.slice(0, 5).map((option) => (
                  <div key={option.value} className="flex items-start mb-1">
                    <input
                      type="checkbox"
                      id={option.value}
                      checked={additionalClinicalIssues.includes(option.value)}
                      onChange={() => handleClinicalIssueChange(option.value)}
                      className="mt-1 h-4 w-4 text-gray-700 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label
                      htmlFor={option.value}
                      className="ml-3 text-md text-gray-700"
                    >
                      {option.label}
                    </label>
                  </div>
                ))}
              </div>

              <div className="w-full min-w-0 h-[93px] gap-[4px] opacity-1 pb-[13px] border-b border-b-gray-100 mb-4">
                {clinicalIssuesOptions.slice(5, 8).map((option) => (
                  <div key={option.value} className="flex items-start mb-1">
                    <input
                      type="checkbox"
                      id={option.value}
                      checked={additionalClinicalIssues.includes(option.value)}
                      onChange={() => handleClinicalIssueChange(option.value)}
                      className="mt-1 h-4 w-4 text-gray-700 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label
                      htmlFor={option.value}
                      className="ml-3 text-md text-gray-700"
                    >
                      {option.label}
                    </label>
                  </div>
                ))}
              </div>

              <div className="w-full min-w-0 gap-[4px] opacity-1">
                {clinicalIssuesOptions.slice(8).map((option) => (
                  <div key={option.value} className="mb-1">
                    <div className="flex items-start">
                      <input
                        type="checkbox"
                        id={option.value}
                        checked={additionalClinicalIssues.includes(option.value)}
                        onChange={() => handleClinicalIssueChange(option.value)}
                        className="mt-1 h-4 w-4 shrink-0 text-gray-700 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label
                        htmlFor={option.value}
                        className="ml-3 text-md text-gray-700"
                      >
                        {option.label}
                      </label>
                    </div>
                    {option.value === "Other" &&
                      additionalClinicalIssues.includes("Other") && (
                        <div ref={otherIssueRef} className="mt-2 w-full pl-7">
                          <PharmacistTipTapEditor
                            value={otherIssue}
                            onChange={setOtherIssue}
                            minHeight="72px"
                            maxHeight="7.5rem"
                            className="border-gray-200"
                            editorWrapClassName="!text-sm !leading-5"
                            hideInlineToolbar={true}
                            ariaLabel="Other clinical issue"
                          />
                        </div>
                      )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="w-full xl:flex-[1.10] min-w-0 h-[645px] rounded-[4px] border border-gray-100 bg-gray-10 flex flex-col box-border">
            <div className="w-full min-w-0 h-[72px] min-h-[72px] max-h-[72px] box-border gap-[10px] opacity-1 pt-[12px] pr-[24px] pb-[12px] pl-[24px] border-t-[1px] border-t-gray-100 border-t-l-[4px] border-t-r-[4px] bg-gray-20 border border-gray-100 flex items-center justify-between">
              <h3 className="text-teal-300 font-semibold text-xl">
                Laboratory (Optional)
              </h3>
            </div>
           
            <div className="flex-1 p-6">
              <div className="w-full min-w-0 gap-[12px] opacity-1 flex flex-col xl:flex-row mb-4">
                  <CustomInput
                    name="laboratoryRecommendations"
                    type="dropdown"
                    options={laboratoryOptions}
                    value={laboratoryRecommendations}
                    onChange={(selected) =>
                      setLaboratoryRecommendations(selected?.value ?? "")
                    }
                    label="Laboratory Recommendation"
                    showOptionalLabel={false}
                    placeholder="Laboratory Recommendation"
                    labelClassName="text-gray-800 font-medium text-[14px]"
                    className="w-full !opacity-100 bg-white"
                  />
                  <CustomInput
                    name="suggestedLabDateTime"
                    className="col-span-12 w-full bg-white xl:max-w-[300px]"
                    label="Suggested Lab Date & Time"
                    labelClassName="!text-sm"
                    type="datetime"
                    value={suggestedLabDateTime}
                    onChange={(e) => setSuggestedLabDateTime(e)}
                    showOptionalLabel={false}
                  />
              </div>

              <div className="w-full min-w-0 h-[150px] opacity-1 border-b border-b-gray-100 mb-4">
                <div className="w-full min-w-0 h-[25px] opacity-100 mb-2">
                  <span 
                    className="font-normal text-[18px] leading-[25px] text-gray-800"
                  >
                    <span className="font-semibold">Reason(s) for lab</span> (Select all that apply):
                  </span>
                </div>
                <div className="space-y-[2px]">
                  {labReasonsOptions.map((option) => (
                    <div key={option.value} className="w-full min-w-0 h-[24px] opacity-1 flex items-center">
                      <input
                        type="checkbox"
                        id={`lab-${option.value}`}
                        checked={reasonsForLab.includes(option.value)}
                        onChange={() => handleLabReasonChange(option.value)}
                        className="mt-1 h-4 w-4 text-gray-800 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label
                        htmlFor={`lab-${option.value}`}
                        className="ml-3 text-[16px] leading-6 text-gray-600 font-medium w-[374px] h-[24px]"
                      >
                        {option.label}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mb-4 w-full min-w-0">
                <PharmacistTipTapEditor
                  value={pharmacistRecommendations}
                  onChange={setPharmacistRecommendations}
                  headerTitle={
                    <span className="text-lg font-semibold leading-[18px] text-teal-300">
                      Lab Suggestions (Optional)
                    </span>
                  }
                  minHeight="120px"
                  maxHeight="200px"
                  className="w-full border border-gray-100 bg-white p-3"
                  ariaLabel="Lab suggestions"
                />
              </div>

              {/* Pharmacist follow up Date optional */}
              <div className="w-full min-w-0 h-[70px] gap-[4px] opacity-1 flex flex-col mb-4">
                <CustomInput
                  name="pharmacistFollowUpDate"
                  className="col-span-12 w-full bg-white"
                  label="Pharmacist: Follow-up Date"
                  labelClassName="!text-sm font-semibold text-teal-300"
                  type="date"
                  value={pharmacistFollowUpDate}
                  onChange={(e) => setPharmacistFollowUpDate(e)}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Buttons */}
      <div className="flex items-center justify-between space-x-4">
        <div className="w-full">{/* Progress indicator could go here */}</div>
        <div className="flex items-center gap-3">
          <Button
            type="primary-outline"
            text="Back"
            className="w-[200px] max-h-[52px] !rounded-[30px]"
            onClick={onPrevious}
            disabled={!canGoPrevious}
          />
          <Button
            type="primary"
            text="Next"
            className="w-[200px] max-h-[52px] !rounded-[30px]"
            onClick={onNext}
            disabled={!canGoNext}
          />
        </div>
      </div>
    </>
  );
};

export default PharmacistFollowUp;
